import {
  Anchor,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notify } from '@/lib/notify';
import {
  formatUsDateShort,
} from '@/lib/dateHelpers';
import {
  IconBuilding,
  IconCalendar,
  IconCamera,
  IconDeviceFloppy,
  IconHeart,
  IconHome,
  IconLock,
  IconShield,
  IconUser,
  IconUsers,
  IconUsersGroup,
  IconActivity,
} from '@tabler/icons-react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type {
  Attachment,
  ContactPoint,
  Extension,
  Location as FhirLocation,
  Practitioner,
  PractitionerRole,
} from '@medplum/fhirtypes';
import {
  User as UserType,
  Role,
  ROLE_LABELS,
  heldRolesForUser,
  InsuranceCredential,
  Practice,
  type PracticeLocation,
} from '../../users/types/rbac';
import { formatLastActive } from '../../users/components/UserTable';
import { fetchAuthMeSnapshot, latestSessionLastSeen } from '../../../lib/securitySettingsService';
import { UserPermissionsTabPanel } from './UserPermissionsTabPanel';
import {
  DraftDobAndZip,
  DraftProfileDemographics,
  type DemographicsDraftHandle,
  type DobZipDraftHandle,
} from './UserSettingsDraftFields';
import { ProviderProfileSettingsFields } from './ProviderProfileSettingsFields';
import {
  EMPTY_PROVIDER_PROFILE_TAB,
} from './providerSettingsFormDefaults';
import { CredentialFormModal } from './CredentialFormModal';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';
import { LocationTileMap } from './LocationTileMap';
import { ProfileSignatureSettings } from './ProfileSignatureSettings';
import {
  createProfileAutosaveController,
  shouldApplyAutosaveResult,
  shouldSkipUnchangedProviderSave,
  shouldValidateDirtyForm,
  statusAfterAutosave,
  type ProfileAutosaveController,
} from './profileAutosave';
import { resolvePermissionsFromProfile, getAssignableRolesForHeldRoles, getAssignmentActorRole, isUserRoleFieldEditable, canEditorAssignRole, canEditMemberProfile } from '../../../features/rbac';
import { setRole, setPrimaryRole } from '../../../lib/adminActionsService';
import { attachmentDataUrl, createSecuredAttachment } from '../../../lib/createSecuredAttachment';
import { PROFILE_RESOURCE_READ_OPTIONS } from '../../../lib/medplumDirectoryUsers';
import {
  RoleTransitionModal,
} from './RoleTransitionModal';
import {
  roleSetChangeNeedsTransitionWizard,
} from '../../../lib/roleTransitionPolicy';
import {
  derivePrimaryRole,
  formatCollapsedRoleLabels,
  holdsSupervisingCapability,
  holdsTreatingCapability,
  normalizeHeldRoles,
  sameRoleSet,
} from '@unify/fhir-model/role-display';
import { usePermissions } from '../../../contexts/PermissionContext';
import type { Client, ClientGroup } from '../../caseload/types';
import type { ProviderAssociation } from '../../clients/data';
import { SaveStatusIndicator, type SaveStatus } from '@/components/SaveStatusIndicator';
import { PractitionerNameText } from '@/components/PractitionerNameText';
import {
  providerSettingsHeadshotFieldLabels,
  providerSettingsHeadshotSchema,
  providerSettingsProfileFieldLabels,
  providerSettingsProfileTabSchema,
  type ProviderSettingsProfileTabFormValues,
} from '@/schemas/practitioner.schema';
import { missingFieldMessage } from '../../../lib/formValidation';
import {
  BIO_EXT,
  CLINICAL_EXT,
  EXT_URLS,
  GENDER_IDENTITY_EXT,
  LGBTQIA_IDENTITIES_EXT,
  NPI_IDENTIFIER_SYSTEM,
  RACE_ETHNICITY_EXT,
  RELIGION_SPIRITUALITY_EXT,
} from '@unify/fhir-model/constants';
import { ADMIN_EXTENSION_URLS } from '../../../lib/adminDirectoryConstants';
import { fetchCreatedAtFromHistory } from '../../../lib/practitionerCreatedAt';
import { getLocationWorkPhone, isPracticeSiteLocation, parseLocationRefId } from '@unify/fhir-model/resources/location';
import {
  getBio,
  getClinical,
  getPractitionerGenderIdentity,
  getPractitionerLgbtqiaIdentities,
  getPractitionerPronouns,
  getPractitionerRaceEthnicity,
  getPractitionerReligionSpirituality,
  getRole,
  setBio,
  setClinical,
  formatPractitionerNameParts,
  resolvePractitionerLegalName,
  resolvePractitionerPreferredNamesForForm,
  stripPractitionerLegacyPreferredNames,
  upsertPractitionerNickname,
} from '@unify/fhir-model/resources/practitioner';
import { getPractitionerRoleCodes } from '@unify/fhir-model/resources/practitioner-role';
import { isUnifyPlatformStaffRole } from '@unify/fhir-model/staff-role-org';
import { applyClientLegalNameToUserUpdates } from './userSettingsSave';
import { getUserFacingErrorMessage, UserFacingError } from '@/lib/userFacingError';
import { omit } from '@/lib/omit';

/** Roles only Unify platform staff assign from this screen. */
const PLATFORM_ASSIGNED_ROLES: ReadonlySet<string> = new Set(['provider', 'supervisor']);

interface UserSettingsProps {
  user: UserType;
  onBack: () => void;
  onUpdateUser: (
    id: string,
    updates: Partial<UserType>,
    options?: {
      silentSuccess?: boolean;
      silentError?: boolean;
      throwOnError?: boolean;
      shouldApplyResult?: () => boolean;
    },
  ) => void | Promise<void>;
  autoSave?: boolean;
  onDeleteUser?: (id: string) => void;
  isPlatformView: boolean;
  allUsers: UserType[];
  practices: Practice[];
  /** When viewing a client, show Profile / Contacts / Insurance */
  client?: Client;
  clientGroups?: ClientGroup[];
  providers?: ProviderAssociation[];
  /** `permissions` renders only the assigned-roles panel for Settings → Security. */
  mode?: 'full' | 'permissions';
}

async function collectPractitionerRoleSlugs(
  medplum: ReturnType<typeof useMedplum>,
  practitionerRoles: PractitionerRole[],
  orgRefFromPractitioner: string | undefined,
): Promise<string[]> {
  const active = practitionerRoles.filter((r) => r.active !== false);
  const orgScoped = orgRefFromPractitioner
    ? active.find((r) => r.organization?.reference === orgRefFromPractitioner)
    : undefined;

  const readCodes = async (pr: PractitionerRole | undefined): Promise<string[]> => {
    const fromSearch = getPractitionerRoleCodes(pr);
    if (fromSearch.length > 0 || !pr?.id) return fromSearch;
    try {
      return getPractitionerRoleCodes(await medplum.readResource('PractitionerRole', pr.id));
    } catch {
      return [];
    }
  };

  const orgCodes = await readCodes(orgScoped);
  if (orgCodes.length > 0) return orgCodes;

  const merged = new Set<string>();
  for (const pr of active) {
    for (const slug of await readCodes(pr)) merged.add(slug);
  }
  return [...merged];
}

type StaffAccountSnapshot = {
  status: UserType['status'];
  role: Role;
  heldRoles: Role[];
};

function deriveStaffAccountFromPractitioner(
  practitioner: Practitioner,
  roleSlugs: string[],
  fallbackRole?: Role,
): StaffAccountSnapshot {
  if (practitioner.active === false) {
    return {
      status: 'Inactive',
      role: (fallbackRole ?? getRole(practitioner) ?? 'provider') as Role,
      heldRoles: [],
    };
  }

  const mergedHeld = normalizeHeldRoles([
    ...resolvePermissionsFromProfile(practitioner).availableRoles,
    ...roleSlugs,
  ]) as Role[];
  const heldRoles = mergedHeld.length > 0
    ? mergedHeld
    : fallbackRole
      ? (normalizeHeldRoles([fallbackRole]) as Role[])
      : [];
  const loginDefault = getRole(practitioner);
  const role = (derivePrimaryRole(heldRoles) ?? loginDefault ?? fallbackRole ?? 'provider') as Role;
  return { status: 'Active', role, heldRoles };
}

async function loadPractitionerRoleSlugs(
  medplum: ReturnType<typeof useMedplum>,
  practitioner: Practitioner,
): Promise<string[]> {
  const orgRefFromPractitioner = practitioner.extension?.find(
    (e) => e.url === ADMIN_EXTENSION_URLS.organization,
  )?.valueReference?.reference;

  try {
    const practitionerRoles = await medplum.searchResources('PractitionerRole', {
      practitioner: `Practitioner/${practitioner.id}`,
      _count: '20',
    });
    return collectPractitionerRoleSlugs(medplum, practitionerRoles, orgRefFromPractitioner);
  } catch {
    return [];
  }
}

function getIdentifierValue(resource: Practitioner, system: string): string {
  return resource.identifier?.find((identifier) => identifier.system === system)?.value ?? '';
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function parseJsonArray(raw?: string): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return raw ? [raw] : [];
  }
}

function removeExtension(resource: Practitioner, url: string): Practitioner {
  return {
    ...resource,
    extension: (resource.extension ?? []).filter((extension) => extension.url !== url),
  };
}

function upsertStringExtension(resource: Practitioner, url: string, value: string): Practitioner {
  const extensions = (resource.extension ?? []).filter((extension) => extension.url !== url);
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return { ...resource, extension: extensions };
  }

  return {
    ...resource,
    extension: [...extensions, { url, valueString: trimmedValue } satisfies Extension],
  };
}

function upsertJsonStringExtension(resource: Practitioner, url: string, values: string[]): Practitioner {
  const extensions = (resource.extension ?? []).filter((extension) => extension.url !== url);
  if (values.length === 0) {
    return { ...resource, extension: extensions };
  }

  return {
    ...resource,
    extension: [...extensions, { url, valueString: JSON.stringify(values) } satisfies Extension],
  };
}

function upsertTelecom(resource: Practitioner, point: ContactPoint): Practitioner {
  const telecom = (resource.telecom ?? []).filter(
    (entry) => !(entry.system === point.system && entry.use === point.use),
  );

  if (!point.value?.trim()) {
    return { ...resource, telecom };
  }

  return {
    ...resource,
    telecom: [...telecom, { ...point, value: point.value.trim() }],
  };
}

function buildHeadshotAttachment(file: File, attachment: Attachment): Attachment {
  return {
    ...attachment,
    title: attachment.title ?? file.name,
    contentType: attachment.contentType ?? file.type,
  };
}

export function getGroupTypeIcon(type: ClientGroup['type']) {
  switch (type) {
    case 'Couples Therapy':
      return <IconHeart size={16} />;
    case 'Family Therapy':
      return <IconHome size={16} />;
    case 'Group Therapy':
    case 'Support Group':
      return <IconUsersGroup size={16} />;
    default:
      return <IconUsers size={16} />;
  }
}

export function getGroupTypeColor(type: ClientGroup['type']): string {
  switch (type) {
    case 'Couples Therapy':
      return 'pink';
    case 'Family Therapy':
      return 'teal';
    case 'Group Therapy':
      return 'violet';
    case 'Support Group':
      return 'blue';
    default:
      return 'gray';
  }
}

/** Scroll the nearest scroll container (settings pane or window) to the profile header. */
function scrollUserProfileToTop(anchor: HTMLElement | null): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      anchor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  });
}

function profilePhotoUrl(photo: Attachment | undefined): string | undefined {
  return photo?.data && photo.contentType ? attachmentDataUrl(photo) : photo?.url;
}

export function UserSettings({
  user,
  onBack: _onBack,
  onUpdateUser,
  autoSave = false,
  isPlatformView: _isPlatformView,
  allUsers: _allUsers,
  practices: _practices,
  client,
  clientGroups: _clientGroups = [],
  providers: _providers = [],
  mode = 'full',
}: UserSettingsProps) {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role: sessionRole, availableRoles, permissions: editorPermissions } = usePermissions();
  const editorRole = getAssignmentActorRole(availableRoles, sessionRole);
  const [editedUser, setEditedUser] = useState(user);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('profile');
  const [editedClient, setEditedClient] = useState<Client | null>(client ?? null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);
  /** Draft bio — kept in a ref so typing does not re-render the full UserSettings tree each keystroke. */
  const bioDraftRef = useRef(user.bio ?? '');
  const preferredFirstDraftRef = useRef(user.clinical?.preferredFirstName ?? '');
  const preferredLastDraftRef = useRef(user.clinical?.preferredLastName ?? '');
  const [staffPreferredInputs, setStaffPreferredInputs] = useState({
    first: user.clinical?.preferredFirstName ?? '',
    last: user.clinical?.preferredLastName ?? '',
  });
  const demographicsDraftRef = useRef<DemographicsDraftHandle>(null);
  const dobZipDraftRef = useRef<DobZipDraftHandle>(null);
  const [autosaveStatus, setAutosaveStatus] = useState<SaveStatus>('saved');
  const autosaveRevisionRef = useRef(0);
  const saveHandlerRef = useRef<(options?: { autosave?: boolean; forcePersist?: boolean }) => Promise<boolean>>(async () => true);
  const autosaveControllerRef = useRef<ProfileAutosaveController | null>(null);
  if (!autosaveControllerRef.current) {
    autosaveControllerRef.current = createProfileAutosaveController(
      async (forcePersist) => {
        const submittedRevision = autosaveRevisionRef.current;
        setAutosaveStatus('saving');
        const saved = await saveHandlerRef.current({ autosave: true, forcePersist });
        setAutosaveStatus((current) => statusAfterAutosave(
          saved,
          submittedRevision,
          autosaveRevisionRef.current,
          current,
        ));
        return saved;
      },
    );
  }

  const scheduleAutosave = useCallback(() => {
    if (!autoSave) return;
    autosaveRevisionRef.current += 1;
    setAutosaveStatus('unsaved');
    autosaveControllerRef.current?.schedule();
  }, [autoSave]);

  const retryAutosave = useCallback(() => {
    setAutosaveStatus('saving');
    void autosaveControllerRef.current?.flush();
  }, []);

  useEffect(() => {
    return () => {
      void autosaveControllerRef.current?.flush();
    };
  }, []);

  const markDraftDirty = useCallback(() => {
    setHasChanges(true);
    scheduleAutosave();
  }, [scheduleAutosave]);
  const profileHydratedVersionRef = useRef<string | undefined>(undefined);

  const profileForm = useForm<ProviderSettingsProfileTabFormValues>({
    initialValues: EMPTY_PROVIDER_PROFILE_TAB,
    validate: zod4Resolver(providerSettingsProfileTabSchema),
  });

  const [credentialing, setCredentialing] = useState<InsuranceCredential[]>(
    user.credentialing || []
  );
  const [providerPractitioner, setProviderPractitioner] = useState<Practitioner | null>(null);
  const [practitionerProfileLoadFailed, setPractitionerProfileLoadFailed] = useState(false);
  const providerPractitionerRef = useRef<Practitioner | null>(null);
  useEffect(() => {
    providerPractitionerRef.current = providerPractitioner;
  }, [providerPractitioner]);
  useEffect(() => {
    if (autoSave && providerPractitioner && (avatarFile || hasChanges)) {
      scheduleAutosave();
    }
  }, [autoSave, providerPractitioner, avatarFile, hasChanges, scheduleAutosave]);
  /** Role codes from org-scoped `PractitionerRole.code` (e.g. provider + supervisor). */
  const [, setPractitionerOrgRoleSlugs] = useState<string[]>([]);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>(
    user.locationIds || []
  );
  const persistedLocationIdsRef = useRef<string[]>(user.locationIds ?? []);
  const userLocationIdsRef = useRef(user.locationIds);
  userLocationIdsRef.current = user.locationIds;
  useEffect(() => {
    // Snapshot when a different user loads, not on every locationIds change (those are unsaved edits).
    persistedLocationIdsRef.current = userLocationIdsRef.current ?? [];
  }, [user.id]);
  const [editingCredential, setEditingCredential] =
    useState<InsuranceCredential | null>(null);
  const [isAddingCredential, setIsAddingCredential] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState<string | null>(
    null
  );
  const isSelf = profile?.id === user.id;
  const [selfLastActive, setSelfLastActive] = useState<string | null>(null);
  const [joinedAt, setJoinedAt] = useState<string | null>(null);
  useEffect(() => {
    if (!isSelf) {
      setSelfLastActive(null);
      return;
    }
    let cancelled = false;
    setSelfLastActive(new Date().toISOString());
    void fetchAuthMeSnapshot(medplum)
      .then((snapshot) => {
        if (cancelled) return;
        setSelfLastActive(latestSessionLastSeen(snapshot.sessions) ?? new Date().toISOString());
      })
      .catch(() => {
        if (cancelled) return;
        setSelfLastActive(new Date().toISOString());
      });
    return () => {
      cancelled = true;
    };
  }, [isSelf, medplum, user.id]);
  useEffect(() => {
    let cancelled = false;
    setJoinedAt(editedUser.createdAt || null);
    if (user.role === 'client') return;
    void fetchCreatedAtFromHistory(medplum, 'Practitioner', user.id)
      .then((createdAt) => {
        if (cancelled) return;
        setJoinedAt(createdAt ?? editedUser.createdAt ?? null);
      })
      .catch(() => {
        if (!cancelled) setJoinedAt(editedUser.createdAt || null);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, user.id, user.role, editedUser.createdAt]);
  const canEditUserRole =
    editorPermissions.edit_users === true
    && isUserRoleFieldEditable(editorRole, user.role, isSelf);
  const [heldRoles, setHeldRoles] = useState<Role[]>(() =>
    normalizeHeldRoles(heldRolesForUser(user)) as Role[],
  );
  const [baselineHeldRoles, setBaselineHeldRoles] = useState<Role[]>(() =>
    normalizeHeldRoles(heldRolesForUser(user)) as Role[],
  );
  const assignableRoles = useMemo(() => {
    const roles = getAssignableRolesForHeldRoles(availableRoles, derivePrimaryRole(heldRoles) ?? user.role);
    if (isUnifyPlatformStaffRole(editorRole)) return roles;
    // Only platform staff assign clinical roles (kept from the Zoom-seat rule, #717, when Zoom was removed).
    return roles.filter((r) => !PLATFORM_ASSIGNED_ROLES.has(r));
  }, [availableRoles, heldRoles, user.role, editorRole]);
  const roleSetDirty = !sameRoleSet(heldRoles, baselineHeldRoles);
  const derivedPrimary = (derivePrimaryRole(heldRoles) ?? user.role) as Role;
  const baselinePrimary = (derivePrimaryRole(baselineHeldRoles) ?? user.role) as Role;
  const [roleTransitionOpen, setRoleTransitionOpen] = useState(false);
  const [roleTransitionMode, setRoleTransitionMode] = useState<'role-change' | 'offboard' | 'reactivate'>('role-change');
  const [pendingHeldRoles, setPendingHeldRoles] = useState<Role[] | null>(null);
  const roleTransitionCommittedRef = useRef(false);
  const pageTopRef = useRef<HTMLDivElement>(null);
  const [roleSaving, setRoleSaving] = useState(false);
  const [defaultRoleSaving, setDefaultRoleSaving] = useState(false);
  /** Bumped after a role write so supervision, assignment history, and the profile re-read. */
  const [roleRefreshToken, setRoleRefreshToken] = useState(0);

  const handleLocationsInit = useCallback((ids: string[]) => {
    setSelectedLocationIds(ids);
    persistedLocationIdsRef.current = [...ids];
  }, []);

  const handleLocationToggle = useCallback(
    (id: string, checked: boolean) => {
      const previous = selectedLocationIds;
      const next = checked
        ? [...new Set([...previous, id])]
        : previous.filter((locationId) => locationId !== id);
      setSelectedLocationIds(next);
      void (async () => {
        try {
          await Promise.resolve(onUpdateUser(user.id, { locationIds: next }));
          persistedLocationIdsRef.current = [...next];
        } catch (error) {
          setSelectedLocationIds(previous);
          notify({
            title: 'Could not update locations',
            error: error,
            message: 'Please try again.',
            type: 'error',
          });
        }
      })();
    },
    [onUpdateUser, selectedLocationIds, user.id],
  );

  const isProvider =
    holdsTreatingCapability(heldRoles) || holdsSupervisingCapability(heldRoles);
  const locationsOrgId =
    editedUser.practiceId
    || providerPractitioner?.extension
      ?.find((e) => e.url === ADMIN_EXTENSION_URLS.organization)
      ?.valueReference?.reference?.replace(/^Organization\//, '')
    || '';

  const persistHeadshot = useCallback(
    async (file: File) => {
      if (!providerPractitioner?.id) {
        throw new Error('Practitioner profile is not loaded.');
      }
      const headshotResult = providerSettingsHeadshotSchema.safeParse({
        avatarFile: file,
        hasExistingHeadshot: Boolean(providerPractitioner.photo?.[0]),
      });
      if (!headshotResult.success) {
        throw new UserFacingError(
          missingFieldMessage(
            headshotResult.error.issues.map((issue) => String(issue.path[0] ?? '')),
            providerSettingsHeadshotFieldLabels,
          ),
        );
      }
      const uploadedHeadshot = await createSecuredAttachment(
        medplum,
        file,
        { reference: `Practitioner/${providerPractitioner.id}` },
        file.name,
        file.type,
      );
      const updated: Practitioner = {
        ...providerPractitioner,
        photo: [buildHeadshotAttachment(file, uploadedHeadshot)],
      };
      const saved = await medplum.updateResource(updated);
      providerPractitionerRef.current = saved;
      setProviderPractitioner(saved);
      medplum.invalidateSearches('Practitioner');
      const savedPhotoUrl = profilePhotoUrl(saved.photo?.[0]);
      if (savedPhotoUrl) {
        setAvatarUrl(savedPhotoUrl);
      }
      setAvatarFile(null);
    },
    [medplum, providerPractitioner],
  );

  const persistRoleSet = async (next: Role[]): Promise<boolean> => {
    if (sameRoleSet(next, baselineHeldRoles)) return true;
    if (!canEditUserRole) {
      notify({
        title: 'Cannot change roles',
        message: 'You do not have permission to manage roles for this user.',
        type: 'error',
      });
      setHeldRoles(baselineHeldRoles);
      return false;
    }
    const primary = derivePrimaryRole(next);
    if (!primary) {
      // Empty set is not a role transition — deactivate instead.
      setHeldRoles(baselineHeldRoles);
      notify({
        title: 'Cannot remove last role',
        message: 'Deactivate the user instead of removing their last role.',
        type: 'warning',
      });
      return false;
    }
    if (!canEditorAssignRole(editorRole, primary)) {
      notify({
        title: 'Cannot change roles',
        message: 'You do not have permission to assign the resulting primary role.',
        type: 'error',
      });
      setHeldRoles(baselineHeldRoles);
      return false;
    }
    if (roleSetChangeNeedsTransitionWizard(baselineHeldRoles, next)) {
      setPendingHeldRoles(next);
      setRoleTransitionMode('role-change');
      setRoleTransitionOpen(true);
      return false;
    }
    setRoleSaving(true);
    try {
      const additional = next.filter((r) => r !== primary);
      await setRole(medplum, user.id, primary, undefined, additional);
      await Promise.resolve(onUpdateUser(user.id, { role: primary, heldRoles: next }));
      setEditedUser((prev) => ({ ...prev, role: primary }));
      setHeldRoles(next);
      setBaselineHeldRoles(next);
      setPractitionerOrgRoleSlugs(next);
      setRoleRefreshToken((n) => n + 1);
      notify({
        title: 'Roles updated',
        message: `${user.name} now holds ${formatCollapsedRoleLabels(next)}.`,
        type: 'success',
      });
      return true;
    } catch (error) {
      setHeldRoles(baselineHeldRoles);
      notify({
        title: 'Role update failed',
        error: error,
        message: 'Could not update roles.',
        type: 'error',
      });
      return false;
    } finally {
      setRoleSaving(false);
    }
  };

  const handleDefaultRoleChange = async (slug: string | null) => {
    if (!isSelf || !slug || !heldRoles.includes(slug as Role)) return;
    if (slug === editedUser.role) return;
    setDefaultRoleSaving(true);
    try {
      const saved = await setPrimaryRole(medplum, user.id, slug);
      setProviderPractitioner(saved);
      // set-primary-role already persisted; skip PractitionerRole refresh.
      await Promise.resolve(onUpdateUser(user.id, { role: slug as Role, heldRoles }));
      setEditedUser((prev) => ({ ...prev, role: slug as Role }));
      notify({
        title: 'Default role updated',
        message: 'Shown when you sign in. Your current session role is unchanged.',
        type: 'success',
      });
    } catch (error) {
      notify({
        title: 'Could not update default role',
        error: error,
        message: 'Please try again.',
        type: 'error',
      });
    } finally {
      setDefaultRoleSaving(false);
    }
  };

  const handleAddCredential = (
    credential: Omit<InsuranceCredential, 'id'>
  ) => {
    const newCredential: InsuranceCredential = {
      ...credential,
      id: Date.now().toString(),
    };
    setCredentialing([...credentialing, newCredential]);
    setIsAddingCredential(false);
    setHasChanges(true);
    scheduleAutosave();
  };

  const handleUpdateCredential = (
    id: string,
    updates: Partial<InsuranceCredential>
  ) => {
    setCredentialing(
      credentialing.map((c) => (c.id === id ? { ...c, ...updates } : c))
    );
    setEditingCredential(null);
    setHasChanges(true);
    scheduleAutosave();
  };

  const handleSave = async (options: { autosave?: boolean; forcePersist?: boolean } = {}): Promise<boolean> => {
    const { autosave = false, forcePersist = false } = options;
    const submittedRevision = autosaveRevisionRef.current;
    if (roleSetDirty) {
      const roleSaved = await persistRoleSet(heldRoles);
      if (!roleSaved) return false;
    }

    const providerProfileDirty = profileForm.isDirty();
    const practitionerToUpdate = providerPractitionerRef.current ?? providerPractitioner;

    if (user.role !== 'client' && !practitionerToUpdate) {
      return false;
    }

    if (isProvider && practitionerToUpdate) {
      if (shouldSkipUnchangedProviderSave(providerProfileDirty, forcePersist)) {
        if (roleSetDirty) {
          setHasChanges(false);
          notify({
            title: 'Saved',
            message: 'Roles were updated.',
            type: 'success',
          });
        }
        return true;
      }

      if (shouldValidateDirtyForm(profileForm.isDirty())) {
        const profileValidation = profileForm.validate();
        if (profileValidation.hasErrors) {
          if (autosave) setAutosaveStatus('invalid');
          if (!autosave) {
            notify({
              title: 'Validation error',
              message: missingFieldMessage(
                Object.keys(profileForm.errors),
                providerSettingsProfileFieldLabels,
              ),
              type: 'error',
            });
          }
          return false;
        }
      }

      const profileValues = profileForm.values;
      try {
        let updated: Practitioner = { ...practitionerToUpdate };

              const preferredFirstName = profileValues.preferredFirstName.trim();
              const preferredLastName = profileValues.preferredLastName.trim();
              updated = upsertPractitionerNickname(updated, {
                first: preferredFirstName,
                last: preferredLastName,
              });

              updated = upsertTelecom(updated, {
                system: 'phone',
                use: 'work',
                value: profileValues.phone,
              });

              updated.address = [
                {
                  use: 'home',
                  line: profileValues.street.trim() ? [profileValues.street.trim()] : undefined,
                  city: profileValues.city.trim() || undefined,
                  state: profileValues.state.trim() || undefined,
                  postalCode: profileValues.zipCode.trim() || undefined,
                },
              ];
              updated.communication = profileValues.languages.length
                ? profileValues.languages.map((language) => ({ text: language }))
                : [];

              updated = profileValues.bio.trim() ? setBio(updated, profileValues.bio.trim()) : removeExtension(updated, BIO_EXT);
              updated = upsertStringExtension(updated, EXT_URLS.pronouns, profileValues.pronouns);
              updated = profileValues.specialties.length || profileValues.modalities.length || profileValues.ageGroups.length
                ? setClinical(updated, {
                  specialties: profileValues.specialties,
                  modalities: profileValues.modalities,
                  ageGroups: profileValues.ageGroups,
                })
                : removeExtension(updated, CLINICAL_EXT);
              updated = upsertStringExtension(updated, GENDER_IDENTITY_EXT, profileValues.genderIdentity);
              updated = upsertStringExtension(updated, LGBTQIA_IDENTITIES_EXT, profileValues.lgbtqiaIdentity);
              updated = upsertJsonStringExtension(updated, RELIGION_SPIRITUALITY_EXT, profileValues.religion);
              updated = upsertJsonStringExtension(updated, RACE_ETHNICITY_EXT, profileValues.raceEthnicity);

              const savedPractitioner = await medplum.updateResource(updated);
              providerPractitionerRef.current = savedPractitioner;
              const savedDisplay = formatPractitionerNameParts(savedPractitioner);

              medplum.invalidateSearches('Practitioner');
              if (shouldApplyAutosaveResult(
                autosave,
                submittedRevision,
                autosaveRevisionRef.current,
              )) {
                setProviderPractitioner(savedPractitioner);
                setEditedUser((prev) => ({
                  ...prev,
                  name: savedDisplay.primary,
                  phone: profileValues.phone || undefined,
                  bio: profileValues.bio || undefined,
                }));
                setAvatarFile(null);
                const savedPhotoUrl = profilePhotoUrl(savedPractitioner.photo?.[0]);
                if (savedPhotoUrl) {
                  setAvatarUrl(savedPhotoUrl);
                }
                profileForm.resetDirty(profileValues);
                setHasChanges(false);
                if (!autosave) {
                  notify({
                    title: 'Saved',
                    message: 'Profile changes were saved.',
                    type: 'success',
                  });
                }
              }
              return true;
            } catch (error) {
              if (autosave) setAutosaveStatus('error');
              notify({
                title: 'Save failed',
                message: `${getUserFacingErrorMessage(error, 'Could not save profile changes.')} Your changes remain unsaved.`,
                type: 'error',
              });
              return false;
            }
    }

    if (shouldSkipUnchangedProviderSave(hasChanges || roleSetDirty, forcePersist)) {
      return true;
    }

    const demographicsFromDraft = demographicsDraftRef.current?.getDemographics();
    const isClientSave = user.role === 'client';
    const clinicalSource = { ...(user.clinical ?? {}) };
    const clinicalForSave = isClientSave
      ? clinicalSource
      : (() => {
          return omit(clinicalSource, 'preferredFirstName', 'preferredLastName');
        })();
    const mergedClinical: NonNullable<UserType['clinical']> = {
      ...clinicalForSave,
      dateOfBirth: dobZipDraftRef.current?.getDateOfBirthYmd() || undefined,
      zipCode: dobZipDraftRef.current?.getZip() || undefined,
    };
    if (demographicsFromDraft) {
      mergedClinical.demographics = demographicsFromDraft;
    }

    const editedWithoutName = omit(editedUser, 'name');
    const updates = applyClientLegalNameToUserUpdates(
      {
        ...(isClientSave ? editedUser : editedWithoutName),
        bio: bioDraftRef.current,
        credentialing,
        locationIds: selectedLocationIds,
        clinical: Object.keys(mergedClinical).length ? mergedClinical : undefined,
        ...(!isClientSave
          ? {
              preferredNames: {
                first: preferredFirstDraftRef.current,
                last: preferredLastDraftRef.current,
              },
            }
          : {}),
      },
      {
        isClientSave,
        editedClient,
        fallbackName: editedUser.name,
        fallbackEmail: editedUser.email,
        fallbackPhone: editedUser.phone,
      },
    );
    try {
      await Promise.resolve(onUpdateUser(
        user.id,
        updates,
        {
          silentSuccess: autosave,
          silentError: true,
          throwOnError: true,
          shouldApplyResult: autosave
            ? () => shouldApplyAutosaveResult(
              true,
              submittedRevision,
              autosaveRevisionRef.current,
            )
            : undefined,
        },
      ));
      persistedLocationIdsRef.current = [...selectedLocationIds];
      if (shouldApplyAutosaveResult(
        autosave,
        submittedRevision,
        autosaveRevisionRef.current,
      )) {
        setHasChanges(false);
        // The staff profile persists through onUpdateUser, so mirror the names we
        // just sent onto the local resource — the header reads them from it.
        const savedPreferred = updates.preferredNames;
        if (savedPreferred) {
          setProviderPractitioner((prev) => (
            prev
              ? stripPractitionerLegacyPreferredNames(upsertPractitionerNickname(prev, savedPreferred))
              : prev
          ));
        }
      }
      return true;
    } catch (error) {
      if (autosave) setAutosaveStatus('error');
      notify({
        title: 'Save failed',
        message: `${getUserFacingErrorMessage(error, 'Could not save profile changes.')} Your changes remain unsaved.`,
        type: 'error',
      });
      return false;
    }
  };

  saveHandlerRef.current = handleSave;

  useEffect(() => {
    if (isSelf && activeTab === 'permissions') {
      setActiveTab('profile');
    }
  }, [isSelf, activeTab]);

  useEffect(() => {
    setEditedClient(client ?? null);
  }, [client]);

  useEffect(() => {
    setEditedUser(user);
    const nextHeld = normalizeHeldRoles(heldRolesForUser(user)) as Role[];
    setHeldRoles(nextHeld);
    setBaselineHeldRoles(nextHeld);
  }, [user]);

  useEffect(() => {
    bioDraftRef.current = user.bio ?? '';
  }, [user.id, user.bio]);

  const applyStaffAccountSnapshot = useCallback((snapshot: StaffAccountSnapshot) => {
    setHeldRoles(snapshot.heldRoles);
    setBaselineHeldRoles(snapshot.heldRoles);
    setPractitionerOrgRoleSlugs(snapshot.heldRoles);
    setEditedUser((prev) => ({ ...prev, status: snapshot.status, role: snapshot.role }));
  }, []);

  const refreshStaffAccountFromServer = useCallback(
    async (fallbackRole?: Role): Promise<StaffAccountSnapshot> => {
      medplum.invalidateSearches('Practitioner');
      medplum.invalidateSearches('PractitionerRole');
      const practitioner = await medplum.readResource('Practitioner', user.id, PROFILE_RESOURCE_READ_OPTIONS);
      setProviderPractitioner(practitioner);
      const roleSlugs = await loadPractitionerRoleSlugs(medplum, practitioner);
      let snapshot = deriveStaffAccountFromPractitioner(practitioner, roleSlugs, fallbackRole);
      if (fallbackRole && snapshot.status === 'Inactive') {
        snapshot = {
          status: 'Active',
          role: fallbackRole,
          heldRoles: normalizeHeldRoles([fallbackRole]) as Role[],
        };
      }
      applyStaffAccountSnapshot(snapshot);
      return snapshot;
    },
    [applyStaffAccountSnapshot, medplum, user.id],
  );

  const isClientUser = user.role === 'client';

  useEffect(() => {
    // Load PractitionerRole codes for every staff user so stacked supervisor/provider codes stay visible.
    if (isClientUser) {
      setProviderPractitioner(null);
      setPractitionerProfileLoadFailed(false);
      setPractitionerOrgRoleSlugs([]);
      setAvatarFile(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setPractitionerProfileLoadFailed(false);
        medplum.invalidateSearches('Practitioner');
        medplum.invalidateSearches('PractitionerRole');
        const practitioner = await medplum.readResource('Practitioner', user.id, PROFILE_RESOURCE_READ_OPTIONS);
        if (cancelled) {
          return;
        }

        const roleSlugs = await loadPractitionerRoleSlugs(medplum, practitioner);
        if (cancelled) {
          return;
        }
        setPractitionerOrgRoleSlugs(roleSlugs);

        const parsedPreferred = resolvePractitionerPreferredNamesForForm(practitioner);
        const staffPreferred = {
          first: parsedPreferred.preferredFirstName,
          last: parsedPreferred.preferredLastName,
        };
        preferredFirstDraftRef.current = staffPreferred.first;
        preferredLastDraftRef.current = staffPreferred.last;
        setStaffPreferredInputs(staffPreferred);

        setProviderPractitioner(practitioner);
        setAvatarFile(null);
        setAvatarUrl(profilePhotoUrl(practitioner.photo?.[0]) ?? null);
      } catch (error) {
        if (!cancelled) {
          setPractitionerProfileLoadFailed(true);
          notify({
            title: 'Could not load provider profile',
            error: error,
            message: 'Failed to load provider details.',
            type: 'error',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isClientUser, medplum, user.id]);

  const providerClinical = useMemo(
    () => (providerPractitioner ? getClinical(providerPractitioner) : undefined),
    [providerPractitioner],
  );
  const legalName = providerPractitioner ? resolvePractitionerLegalName(providerPractitioner) : undefined;
  /** Header shows "Preferred Name (Legal Name)"; legal is dropped when there is no distinct nickname. */
  const headerName = useMemo(() => {
    if (!providerPractitioner) {
      return { primary: editedUser.name, legal: editedUser.legalName };
    }
    return formatPractitionerNameParts(providerPractitioner);
  }, [providerPractitioner, editedUser.name, editedUser.legalName]);
  const providerAddress = providerPractitioner?.address?.[0];
  const providerLanguages = (providerPractitioner?.communication ?? [])
    .map((concept) => concept.text ?? concept.coding?.[0]?.display ?? concept.coding?.[0]?.code ?? '')
    .filter(Boolean);
  const providerNpi = providerPractitioner ? getIdentifierValue(providerPractitioner, NPI_IDENTIFIER_SYSTEM) : '';

  useEffect(() => {
    if (!providerPractitioner) {
      profileHydratedVersionRef.current = undefined;
      return;
    }
    const versionKey = `${providerPractitioner.id}:${providerPractitioner.meta?.versionId ?? providerPractitioner.meta?.lastUpdated ?? 'initial'}`;
    if (profileHydratedVersionRef.current === versionKey) {
      return;
    }
    profileHydratedVersionRef.current = versionKey;
    const nick = resolvePractitionerPreferredNamesForForm(providerPractitioner);
    profileForm.setValues({
      preferredFirstName: nick.preferredFirstName,
      preferredLastName: nick.preferredLastName,
      phone: providerPractitioner.telecom?.find((entry) => entry.system === 'phone' && entry.use === 'work')?.value ?? '',
      street: providerAddress?.line?.[0] ?? '',
      city: providerAddress?.city ?? '',
      state: providerAddress?.state ?? '',
      zipCode: digitsOnly(providerAddress?.postalCode ?? '').slice(0, 5),
      languages: providerLanguages,
      bio: getBio(providerPractitioner) ?? '',
      pronouns: getPractitionerPronouns(providerPractitioner) ?? '',
      genderIdentity: getPractitionerGenderIdentity(providerPractitioner) ?? '',
      lgbtqiaIdentity: getPractitionerLgbtqiaIdentities(providerPractitioner) ?? '',
      religion: parseJsonArray(getPractitionerReligionSpirituality(providerPractitioner)),
      raceEthnicity: parseJsonArray(getPractitionerRaceEthnicity(providerPractitioner)),
      ageGroups: providerClinical?.ageGroups ?? [],
      specialties: providerClinical?.specialties ?? [],
      modalities: providerClinical?.modalities ?? [],
    });
    profileForm.resetDirty();
  }, [providerPractitioner, providerAddress, providerClinical, providerLanguages, profileForm]);

  const providerCanSave = profileForm.isDirty() || roleSetDirty;
  const canEditAvatar =
    !isClientUser
    && Boolean(providerPractitioner)
    && !avatarSaving
    && canEditMemberProfile(editorPermissions, isSelf);

  /** Held functional roles — same source as the Permissions tab. */
  const providerHeaderRoleSlugs = heldRoles;

  const getStatusColor = (status: string) => {
    if (status === 'Active') return 'green';
    if (status === 'Pending') return 'yellow';
    return 'red';
  };

  const permissionsPanel = (
    <Box data-autosave-ignore="">
      <UserPermissionsTabPanel
        userId={editedUser.id}
        role={derivedPrimary}
        heldRoles={heldRoles}
        loginDefaultRole={editedUser.role}
        isSelf={isSelf}
        onLoginDefaultChange={(value) => { void handleDefaultRoleChange(value); }}
        loginDefaultSaving={defaultRoleSaving}
        practiceId={editedUser.practiceId}
        canEditUserRole={canEditUserRole}
        assignableRoles={assignableRoles}
        roleSaving={roleSaving}
        refreshToken={roleRefreshToken}
        onHeldRolesChange={(next) => {
          setHeldRoles(next);
          const primary = derivePrimaryRole(next);
          if (primary) {
            setEditedUser((prev) => ({ ...prev, role: primary }));
          }
          void persistRoleSet(next);
        }}
        practices={_practices}
        allUsers={_allUsers}
        canDeactivate={editorPermissions.edit_users === true && !isSelf}
        isInactive={editedUser.status === 'Inactive'}
        onDeactivate={() => {
          setPendingHeldRoles([]);
          setRoleTransitionMode('offboard');
          setRoleTransitionOpen(true);
        }}
        onReactivate={() => {
          setRoleTransitionMode('reactivate');
          setPendingHeldRoles([]);
          setRoleTransitionOpen(true);
        }}
      />
    </Box>
  );

  return (
    <Container
      size={mode === 'permissions' ? undefined : 'lg'}
      fluid={mode === 'permissions'}
      w={mode === 'permissions' ? '100%' : undefined}
      maw={mode === 'permissions' ? '100%' : undefined}
      py={mode === 'permissions' ? 0 : 'xl'}
      px={mode === 'permissions' ? 0 : undefined}
      style={mode === 'permissions' ? { maxWidth: '100%', width: '100%' } : undefined}
      onChangeCapture={autoSave ? (event) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest?.('[data-autosave-ignore], [role="dialog"]')) return;
        scheduleAutosave();
      } : undefined}
    >
      <div ref={pageTopRef} tabIndex={-1} aria-hidden="true" />
      <Stack gap="lg">
        {mode === 'permissions' ? (
          permissionsPanel
        ) : (
          <>
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>User Profile</Title>
            <Text c="gray.6" size="sm" mt="xs">
              Manage user details, permissions, and account status
            </Text>
          </div>

          {!autoSave && (
            <Button
              leftSection={<IconDeviceFloppy size={18} />}
              onClick={() => void handleSave()}
              disabled={isProvider ? !providerCanSave : !(hasChanges || roleSetDirty)}
            >
              Save Changes
            </Button>
          )}
        </Group>

        {/* User Profile Card */}
        <Card withBorder>
          <Group gap="lg" align="flex-start">
            <input
              ref={avatarFileInputRef}
              data-autosave-ignore=""
              type="file"
              accept="image/*"
              disabled={!canEditAvatar}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                if (!providerPractitioner?.id) {
                  notify({
                    title: 'Cannot upload headshot',
                    message: 'Practitioner profile is still loading. Try again in a moment.',
                    type: 'error',
                  });
                  return;
                }
                const previousUrl = avatarUrl ?? profilePhotoUrl(providerPractitioner.photo?.[0]) ?? null;
                const objectUrl = URL.createObjectURL(file);
                setAvatarUrl(objectUrl);
                setAvatarFile(file);
                setAvatarSaving(true);
                void (async () => {
                  try {
                    await persistHeadshot(file);
                    notify({
                      title: 'Headshot saved',
                      message: 'Professional headshot was updated.',
                      type: 'success',
                    });
                  } catch (error) {
                    setAvatarUrl(previousUrl);
                    setAvatarFile(null);
                    notify({
                      title: 'Could not save headshot',
                      error: error,
                      message: 'Please try again.',
                      type: 'error',
                    });
                  } finally {
                    URL.revokeObjectURL(objectUrl);
                    setAvatarSaving(false);
                  }
                })();
              }}
            />
            <div
              style={{
                position: 'relative',
                cursor: avatarSaving ? 'wait' : canEditAvatar ? 'pointer' : 'default',
                borderRadius: 'var(--mantine-radius-xl)',
                overflow: 'hidden',
                opacity: avatarSaving ? 0.7 : 1,
              }}
              onClick={() => {
                if (canEditAvatar) avatarFileInputRef.current?.click();
              }}
              role="button"
              tabIndex={canEditAvatar ? 0 : -1}
              aria-disabled={!canEditAvatar}
              onKeyDown={(e) => {
                if (canEditAvatar && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  avatarFileInputRef.current?.click();
                }
              }}
              aria-label="Upload profile photo"
              aria-busy={avatarSaving}
            >
              <Avatar
                size="xl"
                radius="xl"
                color="orange"
                src={avatarUrl ?? profilePhotoUrl(providerPractitioner?.photo?.[0])}
              >
                {headerName.primary
                  .split(' ')
                  .map((n) => n[0])
                  .filter(Boolean)
                  .join('')}
              </Avatar>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 'var(--mantine-radius-xl)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0, 0, 0, 0.4)',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '1'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '0'; }}
              >
                <IconCamera size={24} color="white" />
              </div>
            </div>

            <Stack gap="md" style={{ flex: 1 }}>
              <Group justify="space-between" align="flex-start">
                <div>
                  <Title order={3}>
                    {isClientUser ? (
                      <Anchor component={Link} to={`/dashboard/clients/${user.id}`} c="gold.7" inherit>
                        <PractitionerNameText
                          inherit
                          fw="inherit"
                          fz="inherit"
                          primary={headerName.primary || '—'}
                          legal={headerName.legal}
                        />
                      </Anchor>
                    ) : (
                      <PractitionerNameText
                        inherit
                        fw="inherit"
                        fz="inherit"
                        primary={headerName.primary || '—'}
                        legal={headerName.legal}
                      />
                    )}
                  </Title>
                  <Text c="gray.6" size="sm" mt="xs">
                    {editedUser.email}
                  </Text>
                  {isProvider && (
                    <Text c="gray.6" size="xs" mt={4}>
                      Professional headshot
                      <Text component="span" c="red" ml={4}>
                        *
                      </Text>
                      {' — '}
                      {avatarSaving
                        ? 'Saving…'
                        : avatarFile
                          ? avatarFile.name
                          : 'click the photo to upload or update'}
                    </Text>
                  )}
                </div>

                <Badge
                  color={getStatusColor(editedUser.status)}
                  size="lg"
                  variant="light"
                >
                  {editedUser.status}
                </Badge>
              </Group>

              <Grid gutter="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconBuilding size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      Practice:
                    </Text>
                    <Text size="sm" fw={500}>
                      {editedUser.practiceName}
                    </Text>
                  </Group>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconCalendar size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      Joined:
                    </Text>
                    <Text size="sm" fw={500}>
                      {formatUsDateShort(joinedAt ?? editedUser.createdAt) || '—'}
                    </Text>
                  </Group>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconActivity size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      Last Active:
                    </Text>
                    <Text size="sm" fw={500}>
                      {formatLastActive(isSelf ? (selfLastActive ?? editedUser.lastActive) : editedUser.lastActive)}
                    </Text>
                  </Group>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconShield size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      {isClientUser ? 'Role:' : 'Roles:'}
                    </Text>
                    <Text size="sm" fw={500}>
                      {isClientUser
                        ? ROLE_LABELS[editedUser.role]
                        : providerHeaderRoleSlugs.length > 0
                          ? formatCollapsedRoleLabels(providerHeaderRoleSlugs)
                          : 'None'}
                    </Text>
                  </Group>
                </Grid.Col>
              </Grid>
            </Stack>
          </Group>
        </Card>

        {/* Tabs */}
        { !isClientUser && !providerPractitioner && !practitionerProfileLoadFailed ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : (
          <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>
              Profile
            </Tabs.Tab>
            {!isSelf ? (
              <Tabs.Tab value="permissions" leftSection={<IconShield size={16} />}>
                Permissions
              </Tabs.Tab>
            ) : null}
          </Tabs.List>

          {/* Profile Tab: Provider profile form */}
          <Tabs.Panel value="profile" pt="lg" keepMounted>
            {isProvider ? (
              <Paper p={32} withBorder radius="md" shadow="sm">
                <Stack gap={24}>
                  <Text size="sm" c="dimmed">
                    Required fields are marked with{' '}
                    <Text component="span" c="red" inherit>
                      *
                    </Text>
                  </Text>
                  <Stack gap={6}>
                    <Group grow align="flex-start">
                      <TextInput
                        label="Legal first name"
                        value={legalName?.given || '—'}
                        readOnly
                        variant="filled"
                        rightSection={<IconLock size={16} aria-hidden />}
                        rightSectionPointerEvents="none"
                      />
                      <TextInput
                        label="Legal last name"
                        value={legalName?.family || '—'}
                        readOnly
                        variant="filled"
                        rightSection={<IconLock size={16} aria-hidden />}
                        rightSectionPointerEvents="none"
                      />
                    </Group>
                  </Stack>

                  <Group grow align="flex-start">
                    <TextInput
                      label="Date of Birth"
                      value={providerPractitioner?.birthDate ?? '—'}
                      readOnly
                      variant="filled"
                      rightSection={<IconLock size={16} aria-hidden />}
                      rightSectionPointerEvents="none"
                    />
                    <TextInput
                      label="NPI"
                      value={providerNpi || '—'}
                      readOnly
                      variant="filled"
                      rightSection={<IconLock size={16} aria-hidden />}
                      rightSectionPointerEvents="none"
                    />
                  </Group>

                  <ProviderProfileSettingsFields form={profileForm} />
                </Stack>
              </Paper>
            ) : (
              <Paper p={32} withBorder radius="md" shadow="sm">
                <Stack gap={24}>
                  {!isClientUser && (
                    <Group grow align="flex-start">
                      <TextInput
                        label="Legal first name"
                        value={legalName?.given || '—'}
                        readOnly
                        variant="filled"
                        rightSection={<IconLock size={16} aria-hidden />}
                        rightSectionPointerEvents="none"
                      />
                      <TextInput
                        label="Legal last name"
                        value={legalName?.family || '—'}
                        readOnly
                        variant="filled"
                        rightSection={<IconLock size={16} aria-hidden />}
                        rightSectionPointerEvents="none"
                      />
                    </Group>
                  )}
                  <Group grow>
                    <TextInput
                      key={`pfn-${user.id}-${staffPreferredInputs.first}`}
                      label="Preferred First Name"
                      placeholder="Preferred First Name"
                      defaultValue={staffPreferredInputs.first}
                      onChange={(e) => {
                        preferredFirstDraftRef.current = e.currentTarget.value;
                        setHasChanges((prev) => prev || true);
                      }}
                    />
                    <TextInput
                      key={`pln-${user.id}-${staffPreferredInputs.last}`}
                      label="Preferred Last Name"
                      placeholder="Preferred Last Name"
                      defaultValue={staffPreferredInputs.last}
                      onChange={(e) => {
                        preferredLastDraftRef.current = e.currentTarget.value;
                        setHasChanges((prev) => prev || true);
                      }}
                    />
                  </Group>
                  {user.aliases && user.aliases.length > 0 ? (
                    <Text size="sm" c="dimmed">
                      Other names or aliases: {user.aliases.join(', ')}
                    </Text>
                  ) : null}
                  <DraftDobAndZip
                    ref={dobZipDraftRef}
                    userId={user.id}
                    initialClinical={user.clinical}
                    onDirty={markDraftDirty}
                  />
                  <Textarea
                    key={`bio-${user.id}-${user.bio ?? ''}`}
                    label="Bio"
                    placeholder="Bio"
                    minRows={10}
                    maxRows={24}
                    defaultValue={user.bio ?? ''}
                    onChange={(e) => {
                      bioDraftRef.current = e.currentTarget.value;
                      setHasChanges((prev) => prev || true);
                    }}
                  />
                  <Divider color="#E5E7EB" />
                  <DraftProfileDemographics
                    ref={demographicsDraftRef}
                    userId={user.id}
                    initial={user.clinical?.demographics}
                    onDirty={markDraftDirty}
                  />
                </Stack>
              </Paper>
            )}
            {user.role !== 'client' ? (
              <Card withBorder mt="lg">
                <div>
                  <Group gap="sm" align="center" wrap="wrap">
                    <Title order={4}>Practice Locations</Title>
                  </Group>
                  <Text size="sm" c="gray.6" mt="xs">
                    Select the locations that you go to
                  </Text>
                </div>
                <LocationsPanel
                  key={`${locationsOrgId}-${editedUser.id}`}
                  organizationId={locationsOrgId}
                  practitionerId={editedUser.id}
                  selectedLocationIds={selectedLocationIds}
                  onInit={handleLocationsInit}
                  onToggle={handleLocationToggle}
                />
              </Card>
            ) : null}
            {!isClientUser ? (
              <ProfileSignatureSettings
                practitionerId={user.id}
                practitioner={providerPractitioner?.id === user.id ? providerPractitioner : null}
                readOnly={!isSelf}
              />
            ) : null}
          </Tabs.Panel>

          {!isSelf ? (
            <Tabs.Panel value="permissions" pt="lg">
              {activeTab === 'permissions' ? permissionsPanel : null}
            </Tabs.Panel>
          ) : null}
          </Tabs>
        )}
          </>
        )}

        {autoSave && <SaveStatusIndicator status={autosaveStatus} onRetry={retryAutosave} />}

        {/* Credential Form Modal */}
        <CredentialFormModal
          isOpen={isAddingCredential || !!editingCredential}
          onClose={() => {
            setIsAddingCredential(false);
            setEditingCredential(null);
          }}
          onAddCredential={handleAddCredential}
          onUpdateCredential={handleUpdateCredential}
          credential={editingCredential}
        />

        {/* Delete Credential Confirmation Dialog */}
        <DeleteConfirmationDialog
          isOpen={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setCredentialToDelete(null);
          }}
          onConfirm={() => {
            if (credentialToDelete) {
              setCredentialing(
                credentialing.filter((c) => c.id !== credentialToDelete)
              );
              setHasChanges(true);
              scheduleAutosave();
            }
            setShowDeleteDialog(false);
            setCredentialToDelete(null);
          }}
          title="Delete Insurance Credential"
          description="Are you sure you want to delete this insurance credential?"
          confirmText="Delete Credential"
        />

        <RoleTransitionModal
          opened={roleTransitionOpen}
          mode={roleTransitionMode}
          assignableRoles={assignableRoles}
          onClose={() => {
            setRoleTransitionOpen(false);
            setPendingHeldRoles(null);
            setRoleTransitionMode('role-change');
            if (!roleTransitionCommittedRef.current) {
              setHeldRoles(baselineHeldRoles);
              setEditedUser((prev) => ({
                ...prev,
                role: baselinePrimary,
              }));
            }
            roleTransitionCommittedRef.current = false;
          }}
          practitionerId={user.id}
          practitionerName={user.name}
          currentRole={baselinePrimary}
          targetRole={
            roleTransitionMode === 'offboard'
              ? baselinePrimary
              : (derivePrimaryRole(pendingHeldRoles ?? heldRoles) ?? derivedPrimary)
          }
          initialHeldRoles={
            roleTransitionMode === 'offboard' ? [] : (pendingHeldRoles ?? heldRoles)
          }
          baselineHeldRoles={baselineHeldRoles}
          allUsers={_allUsers}
          onCompleted={async (role) => {
            roleTransitionCommittedRef.current = true;
            const completedMode = roleTransitionMode;
            if (completedMode === 'reactivate') {
              const optimistic: StaffAccountSnapshot = {
                status: 'Active',
                role,
                heldRoles: normalizeHeldRoles([role]) as Role[],
              };
              applyStaffAccountSnapshot(optimistic);
              let snapshot = optimistic;
              try {
                snapshot = await refreshStaffAccountFromServer(role);
              } catch {
                /* keep optimistic snapshot */
              }
              await Promise.resolve(
                onUpdateUser(user.id, {
                  status: snapshot.status,
                  role: snapshot.role,
                  heldRoles: snapshot.heldRoles,
                }),
              );
              setRoleRefreshToken((n) => n + 1);
              setRoleTransitionOpen(false);
            } else if (completedMode === 'offboard') {
              let snapshot: StaffAccountSnapshot = {
                status: 'Inactive',
                role,
                heldRoles: [],
              };
              try {
                medplum.invalidateSearches('Practitioner');
                const practitioner = await medplum.readResource('Practitioner', user.id, PROFILE_RESOURCE_READ_OPTIONS);
                setProviderPractitioner(practitioner);
                const roleSlugs = await loadPractitionerRoleSlugs(medplum, practitioner);
                snapshot = deriveStaffAccountFromPractitioner(practitioner, roleSlugs, role);
              } catch {
                /* keep optimistic defaults */
              }
              applyStaffAccountSnapshot(snapshot);
              await Promise.resolve(
                onUpdateUser(user.id, {
                  status: snapshot.status,
                  role: snapshot.role,
                  heldRoles: snapshot.heldRoles,
                }),
              );
              setRoleTransitionOpen(false);
            } else {
              const nextHeld = normalizeHeldRoles(pendingHeldRoles ?? heldRoles) as Role[];
              await Promise.resolve(onUpdateUser(user.id, { role, heldRoles: nextHeld }));
              setEditedUser((prev) => ({ ...prev, role }));
              setHeldRoles(nextHeld);
              setBaselineHeldRoles(nextHeld);
              setPractitionerOrgRoleSlugs(nextHeld);
            }
            setHasChanges(false);
            if (completedMode !== 'reactivate') {
              setRoleRefreshToken((n) => n + 1);
            }
            setRoleTransitionMode('role-change');
            if (completedMode === 'reactivate' || completedMode === 'offboard') {
              scrollUserProfileToTop(pageTopRef.current);
            }
          }}
        />
      </Stack>
    </Container>
  );
}

function fhirSiteToPracticeLocation(loc: FhirLocation): PracticeLocation {
  const line = loc.address?.line ?? [];
  return {
    id: loc.id ?? '',
    name: loc.name ?? '',
    addressLine1: line[0] ?? '',
    addressLine2: line[1],
    city: loc.address?.city ?? '',
    state: loc.address?.state ?? '',
    zipCode: loc.address?.postalCode ?? '',
    phone: getLocationWorkPhone(loc),
    latitude: loc.position?.latitude,
    longitude: loc.position?.longitude,
    status: loc.status === 'inactive' ? 'inactive' : 'active',
  };
}

interface LocationsPanelProps {
  organizationId: string;
  practitionerId: string;
  selectedLocationIds: string[];
  onInit: (ids: string[]) => void;
  onToggle: (id: string, checked: boolean) => void;
}

function LocationsPanel({
  organizationId,
  practitionerId,
  selectedLocationIds,
  onInit,
  onToggle,
}: LocationsPanelProps): JSX.Element {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sites, setSites] = useState<PracticeLocation[]>([]);
  /** After `onInit` runs for a given `organizationId|practitionerId`, do not run again for that key (avoids wiping checkboxes on refetch after save). */
  const onInitAppliedForKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    const loadKey = `${organizationId}|${practitionerId}`;

    if (!organizationId || !practitionerId) {
      setSites([]);
      setLoading(false);
      onInitAppliedForKeyRef.current = undefined;
      return;
    }

    const isFirstFetchForThisKey = onInitAppliedForKeyRef.current !== loadKey;
    if (isFirstFetchForThisKey) {
      setLoading(true);
    }
    setError(null);

    (async () => {
      try {
        const orgParam = `Organization/${organizationId}`;
        const [locList, roleList] = await Promise.all([
          medplum.searchResources('Location', { organization: orgParam, _count: '500' }),
          medplum
            .searchResources('PractitionerRole', {
              practitioner: `Practitioner/${practitionerId}`,
              _count: '20',
            })
            .catch(() => [] as PractitionerRole[]),
        ]);
        if (cancelled) return;
        const sitesOnly = locList.filter(isPracticeSiteLocation);
        setSites(sitesOnly.map(fhirSiteToPracticeLocation));

        const orgScopedRole = roleList.find((r) => r.organization?.reference === orgParam);
        const fromRole = (orgScopedRole?.location ?? [])
          .map((ref) => parseLocationRefId(ref))
          .filter((id): id is string => Boolean(id));

        if (onInitAppliedForKeyRef.current !== loadKey) {
          onInitAppliedForKeyRef.current = loadKey;
          onInit(fromRole);
        }
      } catch (e) {
        if (!cancelled) {
          setError(getUserFacingErrorMessage(e, 'Something went wrong.'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // MedplumClient is stable; including it caused refetches after unrelated parent updates and wiped selections via onInit([]).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when org/practitioner identity changes
  }, [organizationId, practitionerId, onInit]);

  if (loading) {
    return (
      <Center py="md" mt="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Text c="red" size="sm" mt="md">
        {error}
      </Text>
    );
  }

  if (sites.length === 0) {
    return (
      <Text c="gray.6" size="sm" mt="md">
        No locations available for this practice
      </Text>
    );
  }

  return (
    <Stack gap="md" mt="md">
      {sites.map((location) => (
        <Card key={location.id} withBorder p="md" style={{ position: 'relative' }}>
          <Checkbox
            checked={selectedLocationIds.includes(location.id)}
            onChange={(e) => {
              onToggle(location.id, e.currentTarget.checked);
            }}
            style={{ position: 'absolute', top: 16, right: 16 }}
            aria-label={`Select ${location.name}`}
          />
          <Group gap="md">
            <LocationTileMap location={location} size={160} />
            <div>
              <Text fw={500} size="sm">
                {location.name}
              </Text>
              <Text size="xs" c="gray.6" mt="xs">
                {location.addressLine1 ?? (location as { address?: string }).address ?? ''}
                {location.addressLine2 ? `, ${location.addressLine2}` : ''}
              </Text>
              <Text size="xs" c="gray.6">
                {location.city}, {location.state} {location.zipCode}
              </Text>
              {location.phone && (
                <Text size="xs" c="gray.6" mt="xs">
                  {location.phone}
                </Text>
              )}
            </div>
          </Group>
        </Card>
      ))}
    </Stack>
  );
}

